import React, { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { 
  PencilSimple, Trash, Plus, X, Check, 
  Package, Link, MagnifyingGlass 
} from "@phosphor-icons/react";
import { Button } from "../shared/Button";
import { Input, Select } from "../shared/Input";
import { comprimirImagen } from "../../utils/imagenes";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const EMOJIS_TRABAJOS = [
  "🏠", "🚪", "🪟", "🎨", "🔨", "🔧", "⚡", "💧",
  "🚿", "🍳", "🛠️", "📏", "🧱", "🏗️", "🔩", "⚙️",
  "🪛", "🧰", "🔌", "💡", "🔥", "❄️", "🌡️", "🏺"
];

export const EditorTrabajos = ({ adminToken }) => {
  const [trabajos, setTrabajos] = useState([]);
  const [modulos, setModulos] = useState([]);
  const [materiales, setMateriales] = useState([]);
  const [filtroModulo, setFiltroModulo] = useState("");
  const [busqueda, setBusqueda] = useState("");
  const [modalEdicion, setModalEdicion] = useState(null);
  const [modalMateriales, setModalMateriales] = useState(null);
  const [materialesAsociados, setMaterialesAsociados] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    cargarDatos();
  }, []);

  const cargarDatos = async () => {
    try {
      const [trabRes, modRes, matRes] = await Promise.all([
        fetch(`${API}/trabajos`).then(r => r.json()),
        fetch(`${API}/modulos`).then(r => r.json()),
        fetch(`${API}/materiales`, { headers: { "Authorization": `Bearer ${adminToken}` } }).then(r => r.json())
      ]);
      setTrabajos(trabRes);
      setModulos(modRes);
      setMateriales(matRes);
    } catch (err) {
      console.error("Error cargando datos:", err);
    } finally {
      setLoading(false);
    }
  };

  const abrirEdicion = (trabajo) => {
    setModalEdicion({
      ...trabajo,
      icono: trabajo.icono || "🔨"
    });
  };

  const handleFotoChange = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    try {
      const comprimida = await comprimirImagen(file);
      setModalEdicion(prev => ({ ...prev, foto_url: comprimida }));
    } catch (err) {
      console.error("Error comprimiendo imagen:", err);
      alert("No se pudo procesar la foto — probá con otra.");
    }
  };

  const guardarTrabajo = async () => {
    if (!modalEdicion.nombre?.trim()) {
      alert("⚠️ El nombre del trabajo es obligatorio");
      return;
    }
    try {
      const resp = modalEdicion.id
        ? await fetch(`${API}/admin/trabajos/${modalEdicion.id}`, {
            method: "PUT",
            headers: {
              "Content-Type": "application/json",
              "Authorization": `Bearer ${adminToken}`
            },
            body: JSON.stringify(modalEdicion)
          })
        : await fetch(`${API}/admin/trabajos`, {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              "Authorization": `Bearer ${adminToken}`
            },
            body: JSON.stringify(modalEdicion)
          });
      if (!resp.ok) throw new Error(await resp.text());
      cargarDatos();
      setModalEdicion(null);
    } catch (err) {
      console.error("Error guardando trabajo:", err);
      alert("❌ No se pudo guardar el trabajo. Probá de nuevo — tus cambios NO se guardaron.");
    }
  };

  const eliminarTrabajo = async (id) => {
    if (!window.confirm("¿Seguro que quieres eliminar este trabajo?")) return;
    try {
      const resp = await fetch(`${API}/admin/trabajos/${id}`, {
        method: "DELETE",
        headers: { "Authorization": `Bearer ${adminToken}` }
      });
      if (!resp.ok) throw new Error(await resp.text());
      cargarDatos();
    } catch (err) {
      console.error("Error eliminando:", err);
      alert("❌ No se pudo eliminar el trabajo. Probá de nuevo.");
    }
  };

  const abrirGestorMateriales = async (trabajo) => {
    setModalMateriales(trabajo);
    try {
      const res = await fetch(`${API}/trabajos/${trabajo.id}/materiales`);
      const mats = await res.json();
      setMaterialesAsociados(mats);
    } catch (err) {
      console.error("Error cargando materiales asociados:", err);
      setMaterialesAsociados([]);
    }
  };

  const asociarMaterial = async (materialId) => {
    try {
      const material = materiales.find(m => m.id === materialId);
      const resp = await fetch(`${API}/admin/trabajos/${modalMateriales.id}/materiales`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "Authorization": `Bearer ${adminToken}`
        },
        body: JSON.stringify({
          material_id: materialId,
          material_nombre: material.nombre,
          cantidad_por_unidad: 1.0,
          obligatorio: true,
          notas: ""
        })
      });
      if (!resp.ok) throw new Error(await resp.text());
      abrirGestorMateriales(modalMateriales);
    } catch (err) {
      console.error("Error asociando material:", err);
      alert("❌ No se pudo asociar el material. Probá de nuevo.");
    }
  };

  const eliminarMaterialAsociado = async (relacionId) => {
    try {
      const resp = await fetch(`${API}/admin/trabajos/${modalMateriales.id}/materiales/${relacionId}`, {
        method: "DELETE",
        headers: { "Authorization": `Bearer ${adminToken}` }
      });
      if (!resp.ok) throw new Error(await resp.text());
      abrirGestorMateriales(modalMateriales);
    } catch (err) {
      console.error("Error eliminando material:", err);
      alert("❌ No se pudo eliminar el material. Probá de nuevo.");
    }
  };

  const trabajosFiltrados = trabajos
    .filter(t => !filtroModulo || t.modulo === filtroModulo)
    .filter(t => !busqueda || t.nombre.toLowerCase().includes(busqueda.toLowerCase()));

  if (loading) {
    return (
      <div className="container pt-4">
        <div className="text-center py-12">Cargando...</div>
      </div>
    );
  }

  return (
    <div className="container pt-4 pb-24">
      <div className="flex justify-between items-center mb-4">
        <h1 className="text-h2 font-display">🔨 Editor de Trabajos</h1>
        <Button variant="primary" onClick={() => setModalEdicion({
          nombre: "",
          modulo: modulos[0]?.id || "",
          unidad: "ud",
          precio_eco: 0,
          precio_std: 0,
          precio_prem: 0,
          tiempo_base_horas: 1,
          icono: "🔨",
          foto_url: "",
          descripcion: "",
          activo: true
        })}>
          <Plus size={20} /> Nuevo Trabajo
        </Button>
      </div>

      {/* Filtros */}
      <div className="card p-4 mb-4">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <div className="input-group">
            <label className="input-label">
              <MagnifyingGlass size={16} /> Buscar
            </label>
            <input
              className="input"
              placeholder="Nombre del trabajo..."
              value={busqueda}
              onChange={(e) => setBusqueda(e.target.value)}
            />
          </div>
          <Select
            label="Módulo"
            value={filtroModulo}
            onChange={(e) => setFiltroModulo(e.target.value)}
            options={[
              { value: "", label: "Todos los módulos" },
              ...modulos.map(m => ({ value: m.id, label: m.nombre }))
            ]}
          />
        </div>
      </div>

      {/* Lista trabajos */}
      <div className="space-y-2">
        {trabajosFiltrados.map(trabajo => (
          <motion.div
            key={trabajo.id}
            layout
            className="card p-4"
          >
            <div className="flex items-center gap-3">
              {trabajo.foto_url ? (
                <img
                  src={trabajo.foto_url}
                  alt={trabajo.nombre}
                  className="rounded-lg object-cover flex-shrink-0"
                  style={{ width: 44, height: 44 }}
                />
              ) : (
                <div style={{ fontSize: "2rem" }}>{trabajo.icono || "🔨"}</div>
              )}
              <div className="flex-1">
                <div className="font-bold">{trabajo.nombre}</div>
                <div className="text-small text-secondary">
                  {trabajo.modulo} · {trabajo.unidad} · ECO: {trabajo.precio_eco}€ / STD: {trabajo.precio_std}€ / PREM: {trabajo.precio_prem}€
                </div>
              </div>
              <div className="flex gap-2">
                <button
                  className="icon-button"
                  onClick={() => abrirGestorMateriales(trabajo)}
                  title="Gestionar materiales"
                >
                  <Package size={20} />
                </button>
                <button
                  className="icon-button"
                  onClick={() => abrirEdicion(trabajo)}
                >
                  <PencilSimple size={20} />
                </button>
                <button
                  className="icon-button"
                  onClick={() => eliminarTrabajo(trabajo.id)}
                  style={{ color: "var(--error)" }}
                >
                  <Trash size={20} />
                </button>
              </div>
            </div>
          </motion.div>
        ))}
      </div>

      {/* Modal Edición */}
      <AnimatePresence>
        {modalEdicion && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="modal-overlay"
            onClick={() => setModalEdicion(null)}
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              className="modal-content"
              style={{ maxWidth: "600px", maxHeight: "90vh", overflow: "auto" }}
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex justify-between items-center mb-4">
                <h2 className="text-h3 font-display">
                  {modalEdicion.id ? "Editar Trabajo" : "Nuevo Trabajo"}
                </h2>
                <button className="icon-button" onClick={() => setModalEdicion(null)}>
                  <X size={24} />
                </button>
              </div>

              <div className="space-y-3">
                <Input
                  label="Nombre del trabajo"
                  value={modalEdicion.nombre}
                  onChange={(e) => setModalEdicion({ ...modalEdicion, nombre: e.target.value })}
                />

                {/* Foto (opcional, se muestra al cliente en vez del icono) */}
                <div className="input-group">
                  <label className="input-label">Foto (opcional)</label>
                  {modalEdicion.foto_url && (
                    <img
                      src={modalEdicion.foto_url}
                      alt="Vista previa"
                      className="rounded-lg object-cover mb-2"
                      style={{ width: "100%", maxHeight: 160 }}
                    />
                  )}
                  <div className="flex gap-2">
                    <input type="file" accept="image/*" onChange={handleFotoChange} className="input" />
                    {modalEdicion.foto_url && (
                      <Button variant="outline" onClick={() => setModalEdicion({ ...modalEdicion, foto_url: "" })}>
                        Quitar
                      </Button>
                    )}
                  </div>
                  <div className="text-tiny text-secondary mt-1">
                    Si tiene foto, el cliente la ve en vez del icono al elegir este trabajo.
                  </div>
                </div>

                {/* Selector de icono */}
                <div className="input-group">
                  <label className="input-label">Icono</label>
                  <div className="grid grid-cols-8 gap-2">
                    {EMOJIS_TRABAJOS.map(emoji => (
                      <button
                        key={emoji}
                        className="card p-2 card-interactive text-center"
                        style={{
                          fontSize: "1.5rem",
                          border: modalEdicion.icono === emoji ? "2px solid var(--brand-accent)" : "1px solid var(--border-subtle)"
                        }}
                        onClick={() => setModalEdicion({ ...modalEdicion, icono: emoji })}
                      >
                        {emoji}
                      </button>
                    ))}
                  </div>
                </div>

                <Select
                  label="Módulo"
                  value={modalEdicion.modulo}
                  onChange={(e) => setModalEdicion({ ...modalEdicion, modulo: e.target.value })}
                  options={modulos.map(m => ({ value: m.id, label: m.nombre }))}
                />

                <Select
                  label="Unidad"
                  value={modalEdicion.unidad}
                  onChange={(e) => setModalEdicion({ ...modalEdicion, unidad: e.target.value })}
                  options={[
                    { value: "m²", label: "m² (metros cuadrados)" },
                    { value: "ml", label: "ml (metros lineales)" },
                    { value: "ud", label: "ud (unidades)" },
                    { value: "h", label: "h (horas)" },
                    { value: "par", label: "par (pares)" }
                  ]}
                />

                <div className="grid grid-cols-3 gap-2">
                  <Input
                    label="Precio ECO (€)"
                    type="number"
                    step="0.01"
                    value={modalEdicion.precio_eco}
                    onChange={(e) => setModalEdicion({ ...modalEdicion, precio_eco: parseFloat(e.target.value) || 0 })}
                  />
                  <Input
                    label="Precio STD (€)"
                    type="number"
                    step="0.01"
                    value={modalEdicion.precio_std}
                    onChange={(e) => setModalEdicion({ ...modalEdicion, precio_std: parseFloat(e.target.value) || 0 })}
                  />
                  <Input
                    label="Precio PREM (€)"
                    type="number"
                    step="0.01"
                    value={modalEdicion.precio_prem}
                    onChange={(e) => setModalEdicion({ ...modalEdicion, precio_prem: parseFloat(e.target.value) || 0 })}
                  />
                </div>

                <Input
                  label="Tiempo base (horas)"
                  type="number"
                  step="0.5"
                  value={modalEdicion.tiempo_base_horas}
                  onChange={(e) => setModalEdicion({ ...modalEdicion, tiempo_base_horas: parseFloat(e.target.value) || 1 })}
                />

                <div className="input-group">
                  <label className="input-label">Descripción</label>
                  <textarea
                    className="input"
                    rows="3"
                    value={modalEdicion.descripcion}
                    onChange={(e) => setModalEdicion({ ...modalEdicion, descripcion: e.target.value })}
                  />
                </div>

                <div className="flex gap-2 mt-4">
                  <Button variant="primary" className="flex-1" onClick={guardarTrabajo}>
                    <Check size={20} /> Guardar
                  </Button>
                  <Button variant="outline" onClick={() => setModalEdicion(null)}>
                    Cancelar
                  </Button>
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Modal Materiales */}
      <AnimatePresence>
        {modalMateriales && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="modal-overlay"
            onClick={() => setModalMateriales(null)}
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              className="modal-content"
              style={{ maxWidth: "700px", maxHeight: "90vh", overflow: "auto" }}
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex justify-between items-center mb-4">
                <h2 className="text-h3 font-display">
                  <Package size={24} /> Materiales de "{modalMateriales.nombre}"
                </h2>
                <button className="icon-button" onClick={() => setModalMateriales(null)}>
                  <X size={24} />
                </button>
              </div>

              {/* Materiales actuales */}
              <div className="mb-4">
                <div className="text-label mb-2">MATERIALES ASOCIADOS</div>
                {materialesAsociados.length === 0 ? (
                  <div className="text-center text-secondary py-4">
                    No hay materiales asociados aún
                  </div>
                ) : (
                  <div className="space-y-2">
                    {materialesAsociados.map(rel => (
                      <div key={rel.id} className="card p-3 flex justify-between items-center">
                        <div>
                          <div className="font-bold">{rel.material_nombre}</div>
                          <div className="text-small text-secondary">
                            {rel.cantidad_por_unidad} {rel.material_unidad || "ud"} por {modalMateriales.unidad}
                          </div>
                        </div>
                        <button
                          className="icon-button"
                          onClick={() => eliminarMaterialAsociado(rel.id)}
                          style={{ color: "var(--error)" }}
                        >
                          <Trash size={18} />
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Agregar material */}
              <div>
                <div className="text-label mb-2">AGREGAR MATERIAL</div>
                <Select
                  value=""
                  onChange={(e) => e.target.value && asociarMaterial(e.target.value)}
                  options={[
                    { value: "", label: "Selecciona un material..." },
                    ...materiales
                      .filter(m => !materialesAsociados.find(rel => rel.material_id === m.id))
                      .map(m => ({ 
                        value: m.id, 
                        label: `${m.nombre} (${m.categoria})` 
                      }))
                  ]}
                />
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};
